
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface PricingCardProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  color: string;
  onSelect: () => void;
}

const PricingCard = ({
  title,
  price,
  description,
  features,
  isPopular,
  color,
  onSelect,
}: PricingCardProps) => {
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "relative rounded-xl border bg-card p-6 shadow-sm transition-all duration-300 hover:shadow-lg",
        isPopular && "border-primary ring-2 ring-primary"
      )}
    >
      {isPopular && (
        <div className="absolute -top-4 inset-x-0 mx-auto w-fit px-3 py-1 rounded-full text-xs font-medium bg-primary text-white">
          Most Popular
        </div>
      )}

      <div className="mb-5">
        <h3 className="text-xl font-bold">{title}</h3>
        <div className="mt-2 flex items-baseline">
          <span className="text-3xl font-bold">{price}</span>
          <span className="ml-1 text-muted-foreground">/ month</span>
        </div>
        <p className="mt-3 text-muted-foreground text-sm">
          {description}
        </p>
      </div>

      <ul className="mt-6 space-y-3">
        {features.map((feature, i) => (
          <motion.li 
            key={i} 
            className="flex items-start"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <CheckCircle className="mr-2 h-5 w-5 flex-shrink-0 text-primary" />
            <span className="text-sm">{feature}</span>
          </motion.li>
        ))}
      </ul>

      <Button
        onClick={onSelect}
        className={cn(
          "mt-6 w-full transform transition-all duration-300 hover:scale-105",
          color
        )}
      >
        Start Your Campaign
      </Button>
    </motion.div>
  );
};

export default PricingCard;
